import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTabNavigation } from 'hooks/useTabNavigation'
import { TAB_SWITCH_NAVIGATION_SOURCE } from 'hooks/useTabNavigation'
import styles from './tab_menu_bar.module.css'
import { Icon, Popup } from 'semantic-ui-react'
import { normalizeTabWarmPath } from 'tabWarm/registry'
import CustomModal from '../CustomModal'

const readShortcutBarState = () => {
    try {
        return localStorage.getItem('shortcut_bar_state') === 'true'
    } catch (error) {
        return false
    }
}

const readShortcutItems = () => {
    try {
        const raw = JSON.parse(localStorage.getItem('shortcut_list') || '[]')
        return Array.isArray(raw) ? raw : []
    } catch (error) {
        return []
    }
}

const normalizeShortcutPath = (item) => {
    const rawPath = item?.path || item?.to || item?.url || ''
    if (typeof rawPath !== 'string' || rawPath.trim() === '') return null
    return `/${rawPath}`.replace(/\/+/g, '/')
}

export function TabMenuBar({ history }) {
    const { t } = useTranslation()
    const {
        tabs,
        activeTabId,
        tabWarmState,
        scheduleTabWarm,
        activateTab,
        closeTab,
        closeAllTabs,
        closeOtherTabs,
        reorderTabs,
        openTab,
        isAtMaxTabs,
        maxTabs,
    } = useTabNavigation()

    const [closeModalOpen, setCloseModalOpen] = useState(false)
    const [closeWarningText, setCloseWarningText] = useState('')
    const pendingCloseActionRef = useRef(null)

    const confirmTabsClose = useCallback((tabIds, callbackAction) => {
        const closingTabs = tabs.filter((tab) => tabIds.includes(tab.id))
        const dirtyTabs = closingTabs.filter((tab) => tab.dirty)

        if (!dirtyTabs.length) {
            callbackAction()
            return
        }


        const previewTitles = dirtyTabs.slice(0, 3).map((tab) => tab.title).join(', ')
        const remainingCount = dirtyTabs.length - 3
        const warningText = remainingCount > 0
            ? t('Tienes cambios pendientes en {{titles}} y {{count}} más. ¿Seguro que quieres cerrar y perder los cambios?', {
                titles: previewTitles,
                count: remainingCount,
            })
            : t('Tienes cambios pendientes en {{titles}}. ¿Seguro que quieres cerrar y perder los cambios?', {
                titles: previewTitles,
            })

        pendingCloseActionRef.current = callbackAction
        setCloseWarningText(warningText)
        setCloseModalOpen(true)
    }, [tabs, t])

    const scrollRef = useRef(null)
    const [contextMenu, setContextMenu] = useState(null)
    const [showShortcuts, setShowShortcuts] = useState(() => readShortcutBarState())
    const [shortcutItems, setShortcutItems] = useState(() => readShortcutItems())

    const [dragState, setDragState] = useState({
        dragIndex: null,
        overIndex: null,
        dropSide: null,
    })

    const dragIndexRef = useRef(null)

    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const updateScrollState = useCallback(() => {
        if (!scrollRef.current) return
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
        setCanScrollLeft(scrollLeft > 0)
        setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth)
    }, [])

    const handleManualScroll = useCallback((direction) => {
        if (!scrollRef.current) return
        const scrollAmount = 250
        scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' })
    }, [])

    const shortcutTabs = useMemo(() => {
        return shortcutItems
            .map((item, index) => {
                const path = normalizeShortcutPath(item)
                if (!path) return null

                return {
                    id: item?.id || `shortcut-${index}-${path}`,
                    title: item?.name || item?.title || path,
                    path,
                    icon: item?.icon || item?.menuIcon || item?.fa_icon || 'bookmark',
                }
            })
            .filter(Boolean)
    }, [shortcutItems])

    const activeTab = useMemo(() => {
        return tabs.find((tab) => tab.id === activeTabId) || null
    }, [activeTabId, tabs])

    const pushTabHistory = useCallback((path) => {
        if (!history?.push) return
        history.push(path, { navigationSource: TAB_SWITCH_NAVIGATION_SOURCE })
    }, [history])

    const handleShortcutToggle = useCallback(() => {
        setShowShortcuts((prev) => !prev)
    }, [])

    const handleShortcutOpen = useCallback((shortcut) => {
        openTab(
            {
                path: shortcut.path,
                title: shortcut.title,
                icon: shortcut.icon,
            },
            pushTabHistory,
            true
        )
    }, [openTab, pushTabHistory])

    const handleDragStart = useCallback((e, index) => {
        if (index === 0) {
            e.preventDefault()
            return
        }

        dragIndexRef.current = index
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(index))

        requestAnimationFrame(() => {
            setDragState((prev) => ({ ...prev, dragIndex: index }))
        })
    }, [])

    const handleDragOver = useCallback((e, index) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'

        if (index === 0) return

        const rect = e.currentTarget.getBoundingClientRect()
        const midX = rect.left + rect.width / 2
        const dropSide = e.clientX < midX ? 'left' : 'right'

        setDragState((prev) => {
            if (prev.overIndex === index && prev.dropSide === dropSide) return prev
            return { ...prev, overIndex: index, dropSide }
        })
    }, [])

    const handleDragLeave = useCallback((e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragState((prev) => ({ ...prev, overIndex: null, dropSide: null }))
        }
    }, [])

    const handleDrop = useCallback((e, dropIndex) => {
        e.preventDefault()
        const fromIndex = dragIndexRef.current
        if (fromIndex === null || fromIndex === undefined) return
        if (dropIndex === 0) return

        let toIndex = dropIndex
        if (dragState.dropSide === 'right') {
            toIndex = dropIndex + 1
        }

        if (fromIndex < toIndex) {
            toIndex -= 1
        }

        if (fromIndex !== toIndex) {
            reorderTabs(fromIndex, toIndex)
        }

        dragIndexRef.current = null
        setDragState({ dragIndex: null, overIndex: null, dropSide: null })
    }, [dragState.dropSide, reorderTabs])

    const handleDragEnd = useCallback(() => {
        dragIndexRef.current = null
        setDragState({ dragIndex: null, overIndex: null, dropSide: null })
    }, [])

    useEffect(() => {
        try {
            localStorage.setItem('shortcut_bar_state', showShortcuts ? 'true' : 'false')
        } catch (error) {
        }
    }, [showShortcuts])

    useEffect(() => {
        const syncShortcuts = () => {
            setShortcutItems(readShortcutItems())
        }

        window.addEventListener('storage', syncShortcuts)
        window.addEventListener('focus', syncShortcuts)

        return () => {
            window.removeEventListener('storage', syncShortcuts)
            window.removeEventListener('focus', syncShortcuts)
        }
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        const onWheel = (e) => {
            if (el.scrollWidth > el.clientWidth && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                el.scrollLeft += e.deltaY

                const isAtEnd = e.deltaY > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
                const isAtStart = e.deltaY < 0 && el.scrollLeft <= 1

                if (!isAtEnd && !isAtStart) {
                    e.preventDefault()
                }
            }
        }

        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [shortcutTabs.length, tabs.length])

    useEffect(() => {
        updateScrollState()
        window.addEventListener('resize', updateScrollState)
        const el = scrollRef.current
        if (el) {
            el.addEventListener('scroll', updateScrollState, { passive: true })
        }
        return () => {
            window.removeEventListener('resize', updateScrollState)
            if (el) el.removeEventListener('scroll', updateScrollState)
        }
    }, [updateScrollState, tabs.length, shortcutTabs.length])

    useEffect(() => {
        if (!activeTabId || !scrollRef.current) return
        const el = scrollRef.current.querySelector(`[data-tabid="${activeTabId}"]`)
        if (el) {
            el.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' })
        }
    }, [activeTabId])

    const openContextMenu = useCallback((e, tabId) => {
        e.preventDefault()
        setContextMenu({ x: e.clientX, y: e.clientY, tabId })
    }, [])

    const closeContextMenu = useCallback(() => setContextMenu(null), [])

    const retryTabWarm = useCallback((e, path) => {
        e.stopPropagation()
        scheduleTabWarm(path)
        closeContextMenu()
    }, [closeContextMenu, scheduleTabWarm])

    useEffect(() => {
        if (!contextMenu) return
        const handler = () => closeContextMenu()
        window.addEventListener('click', handler)
        return () => window.removeEventListener('click', handler)
    }, [contextMenu, closeContextMenu])

    if (!tabs.length) return null
    

    return (
        <>
            <div className={styles.tabBar}>
                <button 
                    type="button"
                    className={`${styles.scrollButton} ${styles.scrollLeft}`}
                    onClick={() => handleManualScroll('left')}
                    disabled={!canScrollLeft}
                >
                    <Icon name='angle left' style={{ margin: 0 }} />
                </button>

                <div className={styles.tabScroller} ref={scrollRef}>
                    {showShortcuts && shortcutTabs.map((shortcut) => {
                        const isShortcutActive = activeTab?.path === shortcut.path

                        return (
                            <button
                                key={shortcut.id}
                                type='button'
                                className={`${styles.tab}${isShortcutActive ? ` ${styles.tabActive}` : ''}`}
                                title={shortcut.title}
                                onClick={() => handleShortcutOpen(shortcut)}
                            >
                                <span className={styles.tabIcon}>
                                    {typeof shortcut.icon === 'string' && (shortcut.icon.startsWith('http') || shortcut.icon.startsWith('/') || shortcut.icon.startsWith('data:')) ? (
                                        <img src={shortcut.icon} alt='' style={{ width: 14, height: 14, objectFit: 'contain' }} />
                                    ) : (
                                        <Icon name={shortcut.icon} style={{ margin: 0, fontSize: 13 }} />
                                    )}
                                </span>
                                <span className={styles.tabTitle}>{shortcut.title}</span>
                            </button>
                        )
                    })}

                    {tabs.map((tab, index) => {
                        const isActive = tab.id === activeTabId
                        const isDragging = dragState.dragIndex === index
                        const isOver = dragState.overIndex === index && dragState.dragIndex !== null
                        const isDraggable = index !== 0
                        const warmEntry = tabWarmState[normalizeTabWarmPath(tab.path)]
                        const isWarmLoading = warmEntry?.status === 'queued' || warmEntry?.status === 'warming'
                        const hasWarmError = warmEntry?.status === 'error'
                        const warmIndicatorTitle = warmEntry?.status === 'queued'
                            ? t('Precargando en segundo plano...')
                            : isWarmLoading
                                ? t('Cargando datos en segundo plano...')
                                : t('La precarga en segundo plano falló.')

                        let tabClassName = styles.tab
                        if (isActive) tabClassName += ` ${styles.tabActive}`
                        if (isDragging) tabClassName += ` ${styles.tabDragging}`
                        if (isOver && dragState.dropSide === 'left') tabClassName += ` ${styles.tabDropLeft}`
                        if (isOver && dragState.dropSide === 'right') tabClassName += ` ${styles.tabDropRight}`

                        return (
                            <div
                                key={tab.id}
                                data-tabid={tab.id}
                                className={tabClassName}
                                onClick={() => activateTab(tab.id, pushTabHistory)}
                                onContextMenu={(e) => openContextMenu(e, tab.id)}
                                title={tab.title}
                                draggable={isDraggable}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                            >
                                {tab.icon && (
                                    <span 
                                    // className={styles.tabIcon}
                                    >
                                        {typeof tab.icon === 'string' && (tab.icon.startsWith('http') || tab.icon.startsWith('/') || tab.icon.startsWith('data:')) ? (
                                            <img src={tab.icon} alt='' style={{ width: 14, height: 14, objectFit: 'contain' }} />
                                        ) : (
                                            <Icon name={tab.icon} style={{ margin: 0, fontSize: 13 }} />
                                        )}
                                    </span>
                                )}

                                <span className={styles.tabTitle}>{tab.title}</span>

                                {isWarmLoading && (
                                    <span
                                        className={styles.tabWarmIndicator}
                                        title={warmIndicatorTitle}
                                        aria-label={warmIndicatorTitle}
                                    >
                                        <Icon name='spinner' loading style={{ margin: 0, fontSize: 12 }} />
                                    </span>
                                )}

                                {hasWarmError && (
                                    <button
                                        type='button'
                                        className={`${styles.tabWarmIndicator} ${styles.tabWarmError} ${styles.tabWarmRetry}`}
                                        title={warmIndicatorTitle}
                                        aria-label={warmIndicatorTitle}
                                        onClick={(e) => retryTabWarm(e, tab.path)}
                                    >
                                        <Icon name='warning sign' style={{ margin: 0, fontSize: 12 }} />
                                    </button>
                                )}

                                {tab.closable !== false && (
                                    <button
                                        className={styles.tabClose}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            confirmTabsClose([tab.id], () => closeTab(tab.id, pushTabHistory))
                                        }}
                                        title={t('Cerrar')}
                                        aria-label={t('Cerrar {{title}}', { title: tab.title })}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        )
                    })}

                    {isAtMaxTabs && (
                        <Popup
                            trigger={
                                <div className={styles.maxTabsIndicator}>
                                    <Icon name='warning circle' />
                                    {t('Límite de {{maxTabs}} pestañas', { maxTabs })}
                                </div>
                            }
                            content={t('Cierra alguna pestaña para abrir nuevas. Límite: {{maxTabs}} pestañas abiertas.', { maxTabs })}
                            position='bottom right'
                            size='small'
                        />
                    )}
                </div>

                <button 
                    type="button"
                    className={`${styles.scrollButton} ${styles.scrollRight}`}
                    onClick={() => handleManualScroll('right')}
                    disabled={!canScrollRight}
                >
                    <Icon name='angle right' style={{ margin: 0 }} />
                </button>
            </div>

            {contextMenu && (
                <div
                    className={styles.contextMenu}
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={closeContextMenu}
                >
                    {tabs.find((t) => t.id === contextMenu.tabId)?.closable !== false && (
                        <button
                            className={styles.contextMenuItem}
                            onClick={() => confirmTabsClose([contextMenu.tabId], () => closeTab(contextMenu.tabId, pushTabHistory))}
                        >
                            <Icon name='times' />
                            {t('Cerrar pestaña')}
                        </button>
                    )}
                    <button
                        className={styles.contextMenuItem}
                        onClick={() => {
                            const otherTabIds = tabs
                                .filter((tab) => tab.id !== contextMenu.tabId && tab.closable !== false)
                                .map((tab) => tab.id)
                            confirmTabsClose(otherTabIds, () => closeOtherTabs(contextMenu.tabId, pushTabHistory))
                        }}
                    >
                        <Icon name='times circle' />
                        {t('Cerrar otras pestañas')}
                    </button>
                    {(() => {
                        const contextTab = tabs.find((t) => t.id === contextMenu.tabId)
                        const warmEntry = contextTab ? tabWarmState[normalizeTabWarmPath(contextTab.path)] : null
                        if (warmEntry?.status !== 'error' || !contextTab) return null

                        return (
                            <button
                                className={styles.contextMenuItem}
                                onClick={(e) => retryTabWarm(e, contextTab.path)}
                            >
                                <Icon name='redo' />
                                {t('Reintentar precarga')}
                            </button>
                        )
                    })()}
                    <div className={styles.contextMenuDivider} />
                    <button
                        className={styles.contextMenuItem}
                        onClick={() => {
                            const allTabIds = tabs
                                .filter((tab) => tab.closable !== false)
                                .map((tab) => tab.id)
                            confirmTabsClose(allTabIds, () => closeAllTabs(pushTabHistory))
                        }}
                    >
                        <Icon name='times circle outline' />
                        {t('Cerrar todas las pestañas')}
                    </button>
                </div>
            )}

            <CustomModal
                open={closeModalOpen}
                setOpen={setCloseModalOpen}
                headerTitle={t('Cambios sin guardar')}
                content={closeWarningText}
                onAccept={() => {
                    setCloseModalOpen(false)
                    if (pendingCloseActionRef.current) {
                        pendingCloseActionRef.current()
                        pendingCloseActionRef.current = null
                    }
                }}
                onCancel={() => {
                    setCloseModalOpen(false)
                    pendingCloseActionRef.current = null
                }}
            />
        </>
    )
}
